import type { ApiSessionsEnded } from 'src/types/userAuth';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { markSignedOut } from 'src/utils/userSession';
import { isTokenRejected } from 'src/utils/requestError';

/**
 * Ends this browser's session on the server (`POST /user/auth/logout` names the session by the
 * cookie that calls it — api#402 step 1, api#454) and empties the query cache. The api answers with
 * the cookie cleared, so the dealer cabinet in the next tab is signed out by the same press; the
 * hint and the epoch follow here (`markSignedOut`).
 *
 * A cookie the api refused (401/403) is a session already over — ended elsewhere, or expired — and
 * that is the end this press asked for: the hint and the cache go. Any other failure (no network, a
 * 5xx) is a sign-out that did not happen: the cookie is still in the browser, and a tab that merely
 * forgot it would be signed straight back in by its next `/me` — so nothing is marked, the failure
 * reaches the screen, and the screen says so.
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await axios.post(`/user/auth/logout`);
      } catch (error) {
        if (!isTokenRejected(error)) throw error;
      }
    },
    onSuccess: () => {
      markSignedOut();
      queryClient.removeQueries({});
    },
  });
};

/**
 * Ends every session of the account — this browser and all the others (`POST /user/auth/logout/all`,
 * api#402). A linked wallet holds no session since api#407, so there is nothing of a wallet's to
 * end. The api answers how many sessions it ended, with this browser's cookie cleared. The cache is
 * emptied only when it did: a refusal here is not the end of anything, and the screen says so.
 */
export const useLogoutEverywhere = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<ApiSessionsEnded> => {
      const response = await axios.post<ApiSessionsEnded>(`/user/auth/logout/all`);

      return response.data;
    },
    onSuccess: () => {
      markSignedOut();
      queryClient.removeQueries({});
    },
  });
};
